import { Map } from "immutable";
import * as React from 'react';

import {
    SECTION_HEIGHT,
    SLIDE_SECTION_HEIGHT,
    STORYBOARD_SUB_SECTION_WIDTH,
    SUB_SECTION_MARGIN,
} from '../constants';
import { LabelUIData, SubSection, TemplateData, TextFieldUIData } from '../services/template-service';

import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core';


interface Props {
    readonly templateData: TemplateData,
    readonly textFields: Map<string, string>,
    readonly templateTextSupplier: (id: string) => string,
    readonly onHelpClicked: (content: string) => void,
    readonly onTextFieldClicked: (key: string, text: string) => void,
}

interface LabelProps {
    readonly label: LabelUIData,
    readonly sectionWidth: number,
    readonly onClick?: (() => void),
}

interface TextFieldProps {
    readonly textField: TextFieldUIData,
    readonly value: string,
    readonly sectionWidth: number,
    readonly onClick: (() => void),
}

interface SubSectionProps {
    readonly subSection: SubSection,
    readonly sectionWidth: number,
}


const styles = (theme: Theme) => {
    const mainColor = theme.palette.grey[500];
    return createStyles({
        clickable: {
            textDecoration: 'underline',
            '&:hover': {
                cursor: 'pointer'
            }
        },
        fieldLabel: {
            alignmentBaseline: 'middle',
            fill: mainColor,
            fontSize: 32,
            stroke: 'none',
            textAnchor: 'middle',
        },
        fieldPath: {
            stroke: mainColor,
            strokeDasharray: '30, 40',
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
            strokeWidth: 10,
        },
        label: {
            fill: mainColor,
            stroke: 'none'
        },
        textField: {
            fill: mainColor,
            stroke: 'none',
            cursor: 'pointer',
            '&:hover $textFieldEditButton': {
                visibility: 'visible'
            },
        },
        textFieldEditButton: {
            visibility: 'hidden',
        },
        border: {
            stroke: mainColor,
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
            strokeWidth: 10,
            fill: 'none',
        },
        hotspot: {
            fill: '#eee',
        },
        hotspotOverlay: {
            fill: 'white',
        },
        separator: {
            stroke: mainColor,
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
            strokeWidth: 10,
            strokeDasharray: '30, 40',
        },
        root: {
            fontFamily: '"Gloria Hallelujah", cursive',
            userSelect: 'none',
        },
    });
};

type Styles =
    'root'
    | 'border'
    | 'hotspot'
    | 'hotspotOverlay'
    | 'separator'
    | 'label'
    | 'textField'
    | 'textFieldEditButton'
    | 'editButton'
    | 'fieldPath'
    | 'fieldLabel'
    | 'clickable';


const TemplateView = ({templateData, templateTextSupplier, textFields, classes, onHelpClicked, onTextFieldClicked}: Props & WithStyles<Styles>) => {

    function LabelView({label, sectionWidth, onClick}: LabelProps) {
        const anchor = label.anchor === 'START' ? 'start' : label.anchor === 'END' ? 'end' : 'middle';
        const align = label.align === 'TOP' ? 'text-before-edge' : label.align === 'BOTTOM' ? 'text-after-edge' : 'middle';
        const fontSize = label.size === 'LARGE' ? 64 : label.size === 'SMALL' ? 32 : 48;
        const x = label.storyboardPosition.anchor === 'LEFT'? label.storyboardPosition.dx
            : label.storyboardPosition.anchor === 'RIGHT'? sectionWidth - label.storyboardPosition.dx
                : sectionWidth / 2;
        return (
            <text
                className={`${classes.label} ${onClick ? classes.clickable : ''}`}
                y={label.storyboardPosition.dy - 2 * fontSize}
                textAnchor={anchor}
                dominantBaseline={align}
                fontSize={fontSize}
                onClick={onClick}
            >
                {label.texts.map(
                    (text, textIndex) => (
                        <tspan
                            key={`text-${textIndex}`}
                            x={x}
                            dy={1.5 * fontSize}
                        >
                            {templateTextSupplier(text)}
                        </tspan>
                    )
                )}
            </text>
        );
    }

    function TextFieldView({textField, sectionWidth, value, onClick}: TextFieldProps) {
        const anchor = textField.anchor === 'START' ? 'start' : textField.anchor === 'END' ? 'end' : 'middle';
        const align = textField.align === 'TOP' ? 'text-before-edge' : textField.align === 'BOTTOM' ? 'text-after-edge' : 'middle';
        const fontSize = textField.size === 'LARGE' ? 64 : textField.size === 'SMALL' ? 16 : 32;
        const x = textField.storyboardPosition.anchor === 'LEFT'? textField.storyboardPosition.dx
            : textField.storyboardPosition.anchor === 'RIGHT'? sectionWidth - textField.storyboardPosition.dx
                : sectionWidth / 2;
        return (
            <g className={classes.textField} onClick={onClick}>
                <text
                    x={x}
                    y={textField.storyboardPosition.dy}
                    textAnchor={anchor}
                    dominantBaseline={align}
                    fontSize={fontSize}
                >
                    {value || textField.defaultValue}
                </text>
                {/*<g className={classes.textFieldEditButton}*/}
                   {/*transform={`matrix(3 0 0 3 ${x + 80} ${textField.position.dy - 50})`}>*/}
                    {/*<path fill="none" d="M0 0h24v24H0V0z"/>*/}
                    {/*<path*/}
                        {/*d="M3 17.46v3.04c0 .28.22.5.5.5h3.04c.13 0 .26-.05.35-.15L17.81 9.94l-3.75-3.75L3.15 17.1c-.1.1-.15.22-.15.36zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>*/}
                {/*</g>*/}
            </g>
        );
    }

    function SubSectionView({subSection, sectionWidth}: SubSectionProps) {
        const x = subSection.storyboardPosition.anchor === 'LEFT'? subSection.storyboardPosition.dx :
            sectionWidth - subSection.storyboardPosition.dx;
        return (
            <g transform={`translate(${x} ${SUB_SECTION_MARGIN})`}>
                <path
                    className={classes.fieldPath}
                    fill={subSection.hovered ? '#ddd' : 'none'}
                    d={subSection.storyboardPath}
                />

                {subSection.labels && subSection.labels
                    .map((label, labelIndex) => (
                        <LabelView key={labelIndex} label={label} sectionWidth={STORYBOARD_SUB_SECTION_WIDTH} />
                    ))
                }

                <LabelView
                    label={subSection.help.button}
                    sectionWidth={STORYBOARD_SUB_SECTION_WIDTH}
                    onClick={() => onHelpClicked(subSection.help.content)}
                />
            </g>
        );

    }

    return (
        <g className={classes.root}>
            {templateData.sections.map(
                (section, sectionIndex) => (
                    <g
                        key={`section-${sectionIndex}`}
                        transform={`translate(${section.storyboardData.minX} ${section.storyboardData.yTop})`}
                    >
                        { (section.noteHovered || section.slideHovered) &&
                            <path
                                className={classes.hotspot}
                                d={section.storyboardData.borderPath}
                            />
                        }

                        { section.noteHovered &&
                        <path
                            className={classes.hotspotOverlay}
                            d={`${section.storyboardData.separatorPath} h100v${-SLIDE_SECTION_HEIGHT-100}h${-section.storyboardData.width-200}v${SLIDE_SECTION_HEIGHT+100}z`}
                        />
                        }

                        { section.slideHovered &&
                        <path
                            className={classes.hotspotOverlay}
                            d={`${section.storyboardData.separatorPath} h100v${SECTION_HEIGHT+100}h${-section.storyboardData.width-200}v${-SECTION_HEIGHT-100}z`}
                        />
                        }
                    </g>
                )
            )}
            {templateData.sections.map(
                (section, sectionIndex) => (
                    <g
                        key={`section-${sectionIndex}`}
                        transform={`translate(${section.storyboardData.minX} ${section.storyboardData.yTop})`}
                    >
                        <path
                            className={classes.border}
                            d={section.storyboardData.borderPath}
                        />

                        <path
                            className={classes.separator}
                            fill={section.noteHovered ? '#eee' : 'none'}
                            d={section.storyboardData.separatorPath}
                        />

                        <g transform={`translate(0 ${SLIDE_SECTION_HEIGHT})`}>

                            {section.labels
                                .map((label, labelIndex) => (
                                    <LabelView key={labelIndex} label={label} sectionWidth={section.storyboardData.width} />
                                ))
                            }

                            {section.textFields
                                .map((textField, textFieldIndex) => (
                                    <TextFieldView key={textFieldIndex}
                                                   textField={textField}
                                                   value={textFields.get(textField.key, "")}
                                                   sectionWidth={section.storyboardData.width}
                                                   onClick={() => onTextFieldClicked(textField.key, textFields.get(textField.key, ""))}
                                    />
                                ))
                            }

                            {section.subSections
                                .map((noteField, noteFieldIndex) => (
                                    <SubSectionView key={noteFieldIndex}
                                                    subSection={noteField}
                                                    sectionWidth={section.storyboardData.width}
                                    />
                                ))
                            }

                            <LabelView
                                label={section.help.button}
                                sectionWidth={section.storyboardData.width}
                                onClick={() => onHelpClicked(section.help.content)}
                            />

                        </g>
                    </g>
                )
            )}
        </g>
    );
};

export default withStyles(styles)(TemplateView);


// WEBPACK FOOTER //
// ./src/components/StoryboardTemplateView.tsx